<template>
  <div class="risk-scoring-factor-field">
    <div class="flex flex-col">
      <div
        class="flex items-center h-12 px-2 rounded"
        :class="{
          'bg-red-50': criticality === 'sanction' && level === 'high'
        }"
      >
        <RiskScoringLabel v-if="criticality !== 'sanction'" :level="level" />

        <span
          class="ml-2 text-gray-400 flex-1"
          :class="{'text-red-600': criticality === 'sanction' && level === 'high'}"
        >
          {{ message }}
        </span>

        <span v-if="criticality !== 'sanction'"
          class="ml-2 text-gray-400 flex-1"
          :class="{'text-red-600': criticality === 'sanction' && level === 'high'}"
        >
          {{ score }} {{ $gettext("pts") }}
        </span>

        <Switch
          v-model="internalSkipped"
          :class="[
            'relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none',
            !internalSkipped ? 'bg-indigo-500' : 'bg-gray-300'
          ]"
        >
          <span
            :class="[
              'inline-block h-4 w-4 transform rounded-full bg-white transition-transform',
              !internalSkipped ? 'translate-x-6' : 'translate-x-1'
            ]"
          ></span>
        </Switch>
      </div>

      <!-- Paragraph below -->
      <p
        v-if="criticality === 'sanction' && level === 'high'"
        class="flex items-start gap-2 rounded bg-red-50 p-2"
      >
        <ExclamationTriangleIcon class="text-red-600 h-6 w-6 flex-shrink-0" />
        <span class="text-red-600 text-sm">
          {{ $gettext("Attention, ce prospect ou ce client fait l'objet de sanctions. Vous ne pouvez légalement pas contracter. Si il s'agit d'un faux positif, n'oubliez pas d'encoder une raison valable en désactivant cette vérification.") }}
        </span>
      </p>

    </div>


    <!-- Textarea if skipped -->
    <div v-if="internalSkipped" class="mt-4 px-2" :class="{
          'bg-red-50 p-4': criticality === 'sanction' && level === 'high'
        }">
      <label class="block text-sm font-medium text-gray-700 mb-1">
        {{ $gettext("Ce n'est pas applicable parce que:") }}
      </label>
      <textarea
        v-model="localJustification"
        rows="4"
        class="h-16 block w-full rounded-md sm:text-sm p-4"
        :class="[
          'border',
          error
            ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
            : 'border-gray-600'
        ]"
        :placeholder="$gettext('Expliquez pourquoi vous souhaitez désactiver cette règle...')"
      ></textarea>

      <p v-if="error" class="text-red-500 text-sm mt-1">
        {{ $gettext("Une justification est obligatoire lorsqu'une règle est désactivée.") }}
      </p>
    </div>
  </div>
</template>

<script setup>
import { ref, watch } from "vue";
import { Switch } from "@headlessui/vue";
import RiskScoringLabel from "@/components/risk-scorings/RiskScoringLabel.vue";
import { ExclamationTriangleIcon } from "@heroicons/vue/24/outline";

const props = defineProps({
  id: String,
  score: Number,
  level: String,
  message: String,
  skipped: Boolean,
  criticality: String,
  justification: String,
  error: Boolean // 🔹 NEW: comes from parent validation
});

const emit = defineEmits(["update:skipped", "update:justification"]);

const internalSkipped = ref(props.skipped);
const localJustification = ref(props.justification);

// Sync parent → local
watch(() => props.skipped, val => internalSkipped.value = val);
watch(() => props.justification, val => localJustification.value = val);

// Sync local → parent
watch(internalSkipped, val => {
  if (!val) {
    // keep justification if user toggles back ON
    emit("update:skipped", val);
    return;
  }
  emit("update:skipped", val);
});
watch(localJustification, val => emit("update:justification", val));
</script>
